import React, { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";

const STATE_MAP = {
  alabama:"AL",alaska:"AK",arizona:"AZ",arkansas:"AR",california:"CA",colorado:"CO",connecticut:"CT",
  delaware:"DE",florida:"FL",georgia:"GA",hawaii:"HI",idaho:"ID",illinois:"IL",indiana:"IN",iowa:"IA",
  kansas:"KS",kentucky:"KY",louisiana:"LA",maine:"ME",maryland:"MD",massachusetts:"MA",michigan:"MI",
  minnesota:"MN",mississippi:"MS",missouri:"MO",montana:"MT",nebraska:"NE",nevada:"NV","new hampshire":"NH",
  "new jersey":"NJ","new mexico":"NM","new york":"NY","north carolina":"NC","north dakota":"ND",ohio:"OH",
  oklahoma:"OK",oregon:"OR",pennsylvania:"PA","rhode island":"RI","south carolina":"SC","south dakota":"SD",
  tennessee:"TN",texas:"TX",utah:"UT",vermont:"VT",virginia:"VA",washington:"WA","west virginia":"WV",
  wisconsin:"WI",wyoming:"WY","district of columbia":"DC",
};

// Lightweight US address autocomplete via OpenStreetMap Nominatim (no API key).
export function AddressField({ label = "Property Address", value, onChange, onSelect, className = "", testId }) {
  const [q, setQ] = useState(value || "");
  const [sug, setSug] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef();
  const box = useRef();

  useEffect(() => { setQ(value || ""); }, [value]);
  useEffect(() => {
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const handle = (val) => {
    setQ(val);
    onChange(val);
    clearTimeout(timer.current);
    if (!val || val.length < 4) { setSug([]); setOpen(false); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const r = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&countrycodes=us&limit=5&q=${encodeURIComponent(val)}`,
          { headers: { "Accept-Language": "en" } }
        );
        const d = await r.json();
        setSug(Array.isArray(d) ? d : []);
        setOpen(true);
      } catch { setSug([]); }
      finally { setLoading(false); }
    }, 350);
  };

  const pick = (s) => {
    onChange(s.display_name);
    setQ(s.display_name);
    setOpen(false);
    if (onSelect && s.address) {
      const a = s.address;
      const state = STATE_MAP[(a.state || "").toLowerCase()] || a.state || "";
      onSelect({ state, postcode: a.postcode || "", city: a.city || a.town || a.village || "" });
    }
  };

  return (
    <div className={`relative ${className}`} ref={box}>
      <label className="text-sm font-medium text-white/80 mb-1.5 block">{label}</label>
      <div className="relative">
        <MapPin className="w-4 h-4 text-gold absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <Input
          value={q}
          onChange={(e) => handle(e.target.value)}
          onFocus={() => sug.length && setOpen(true)}
          autoComplete="off"
          placeholder="Start typing an address…"
          className="bg-surface/70 border-edge text-white h-11 pl-9"
          data-testid={testId}
        />
        {loading && <Loader2 className="w-4 h-4 text-gold animate-spin absolute right-3 top-1/2 -translate-y-1/2" />}
      </div>
      {open && sug.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full rounded-xl border border-edge bg-charcoal shadow-xl overflow-hidden max-h-64 overflow-y-auto" data-testid={`${testId}-suggestions`}>
          {sug.map((s) => (
            <li key={s.place_id}>
              <button type="button" onClick={() => pick(s)}
                className="w-full text-left px-4 py-2.5 text-sm text-white/85 hover:bg-gold/10 hover:text-gold flex items-start gap-2">
                <MapPin className="w-3.5 h-3.5 text-gold mt-0.5 shrink-0" /> {s.display_name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
